import type { UIMessage } from 'ai';
import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import type { ZodSchema } from 'zod';
import type { Document } from '@/lib/db/schema';
import { ChatSDKError, type DetailKey, type ErrorCode } from './errors';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const fetcher = async (url: string) => {
  const response = await fetch(url);

  if (!response.ok) {
    const status = response.status;
    const body = (await response.json().catch(() => ({}))) as {
      code?: string;
      cause?: string;
      detail?: DetailKey;
      error?: string;
    };
    // Routes on the ChatSDKError shape carry a `code`; keep throwing that.
    // Legacy routes return `{ error }` with no `code` — constructing
    // ChatSDKError(undefined) yields a status-less error, so fall back to a
    // plain Error. Either way attach the HTTP status so the global SWR policy
    // recognizes a 401/403 regardless of body shape (ENG-1242).
    const error = (
      body.code
        ? new ChatSDKError(body.code as ErrorCode, {
            cause: body.cause,
            detail: body.detail,
          })
        : new Error(body.error ?? `Request failed with status ${status}`)
    ) as Error & { status?: number };
    error.status = status;
    throw error;
  }

  return response.json();
};

/**
 * Extracts error message from a failed response
 */
async function extractErrorMessage(response: Response): Promise<string> {
  let errorMessage = `Request failed with status ${response.status}`;
  try {
    const errorData = await response.json();
    errorMessage = errorData?.error || errorData?.message || errorMessage;
  } catch {
    // If JSON parsing fails, use the default error message
  }
  return errorMessage;
}

/**
 * Error thrown by client `fetch` wrappers that PRESERVES the HTTP status (and,
 * when present, the ChatSDKError `code`). This is the central seam that lets the
 * global SWR policy in `components/common/swr-provider.tsx` treat a 401/403 as
 * terminal and drop a dead session to login (ENG-1242).
 *
 * Any bespoke `useSWR` fetcher should throw via {@link throwOnFetchError} rather
 * than a bare `new Error(...)`, which discards the status and re-introduces the
 * zombie-UI / retry-storm bug.
 */
export class FetchError extends Error {
  readonly status: number;
  readonly code?: string;

  constructor(message: string, status: number, code?: string) {
    super(message);
    this.name = 'FetchError';
    this.status = status;
    this.code = code;
  }
}

/**
 * Reads a failed `Response` and throws a {@link FetchError} carrying its HTTP
 * status. Handles every error-body shape the app returns — `{ code, message }`
 * (ChatSDKError contract), `{ error }` (legacy/admin routes), or an
 * unreadable/empty body — because the status is taken from `response.status`
 * itself rather than inferred from the body.
 *
 * Returns `Promise<never>`; callers can `return throwOnFetchError(response)`.
 */
export async function throwOnFetchError(response: Response): Promise<never> {
  let code: string | undefined;
  let message = `Request failed with status ${response.status}`;
  try {
    const body = await response.json();
    if (body && typeof body === 'object') {
      if (typeof body.code === 'string' && body.code.length > 0) {
        code = body.code;
      }
      message = body.message || body.error || message;
    }
  } catch {
    // Non-JSON or empty body — keep the status-based default message.
  }
  throw new FetchError(message, response.status, code);
}

export const fetcherWithSchema = async <T>(
  url: string,
  schema: ZodSchema<T>,
) => {
  const response = await fetch(url);

  if (!response.ok) {
    // Surface the HTTP status on the thrown error so the global SWR
    // `onErrorRetry`/`onError` policy can treat 401/403 as terminal and drop a
    // dead session to login (ENG-1242). The message contract is unchanged.
    const error = new Error(await extractErrorMessage(response)) as Error & {
      status?: number;
    };
    error.status = response.status;
    throw error;
  }

  return schema.parse(await response.json());
};

/**
 * Mutation fetcher with schema validation
 * Similar to fetcherWithSchema but supports custom fetch options (method, body, etc.)
 * Use this for POST/PATCH/DELETE mutations
 */
export const mutationFetcherWithSchema = async <T>(
  url: string,
  schema: ZodSchema<T>,
  options: RequestInit,
) => {
  const response = await fetch(url, options);

  if (!response.ok) {
    const errorData = await response.json();

    // Handle ChatSDKError contract: { code, message, detail? }
    if (errorData.code && errorData.message) {
      throw new ChatSDKError(errorData.code as ErrorCode, {
        detail: errorData.detail as DetailKey | undefined,
      });
    }

    // Handle legacy format: { error, details? } - map to appropriate error code
    if (errorData.error) {
      const errorCode: ErrorCode =
        response.status === 401
          ? 'unauthorized:api'
          : response.status === 403
            ? 'forbidden:api'
            : response.status === 404
              ? 'not_found:api'
              : 'bad_request:api';
      throw new ChatSDKError(errorCode, errorData.error);
    }

    // Fallback for unknown error formats
    throw new ChatSDKError(
      'bad_request:api',
      await extractErrorMessage(response),
    );
  }

  return schema.parse(await response.json());
};

export async function fetchWithErrorHandlers(
  input: RequestInfo | URL,
  init?: RequestInit,
) {
  try {
    const response = await fetch(input, init);

    if (!response.ok) {
      const errorData = await response.json();
      const error = new ChatSDKError(
        errorData.code as ErrorCode,
        errorData.cause,
      ) as ChatSDKError & {
        action?: unknown;
        denialReason?: unknown;
        resetAt?: unknown;
        limitType?: unknown;
        used?: unknown;
        limit?: unknown;
        windowHours?: unknown;
      };

      if (typeof errorData.message === 'string') {
        error.message = errorData.message;
      }

      if ('action' in errorData) {
        error.action = errorData.action;
      }

      if ('denialReason' in errorData) {
        error.denialReason = errorData.denialReason;
      }

      if ('resetAt' in errorData) {
        error.resetAt = errorData.resetAt;
      }

      if ('limitType' in errorData) {
        error.limitType = errorData.limitType;
      }

      if ('used' in errorData) {
        error.used = errorData.used;
      }

      if ('limit' in errorData) {
        error.limit = errorData.limit;
      }

      if ('windowHours' in errorData) {
        error.windowHours = errorData.windowHours;
      }

      throw error;
    }

    return response;
  } catch (error: unknown) {
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      throw new ChatSDKError('offline:chat');
    }

    throw error;
  }
}

export function getLocalStorage(key: string) {
  if (typeof window !== 'undefined') {
    return JSON.parse(localStorage.getItem(key) || '[]');
  }
  return [];
}

export function generateUUID(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

type ResponseMessage = { id: string };

export function getMostRecentUserMessage(messages: Array<UIMessage>) {
  const userMessages = messages.filter((message) => message.role === 'user');
  return userMessages.at(-1);
}

export function getDocumentTimestampByIndex(
  documents: Array<Document>,
  index: number,
) {
  if (!documents) return new Date();
  if (index > documents.length) return new Date();

  return documents[index].createdAt;
}

export function getTrailingMessageId({
  messages,
}: {
  messages: Array<ResponseMessage>;
}): string | null {
  const trailingMessage = messages.at(-1);

  if (!trailingMessage) return null;

  return trailingMessage.id;
}

export function sanitizeText(text: string) {
  return text.replace('<has_function_call>', '');
}

/**
 * Mask an email address for safe logging (PII protection).
 * Shows first 2 characters of local part, masks the rest, and shows full domain.
 *
 * @example
 * maskEmail('john.doe@example.com') // 'jo***@example.com'
 * maskEmail('ab@test.org') // 'ab***@test.org'
 * maskEmail('a@x.co') // 'a***@x.co'
 */
export function maskEmail(email: string): string {
  const atIndex = email.indexOf('@');
  if (atIndex === -1) {
    // Not a valid email, return masked version
    return email.length > 2 ? `${email.slice(0, 2)}***` : '***';
  }

  const localPart = email.slice(0, atIndex);
  const domain = email.slice(atIndex);

  // Show first 2 characters of local part (or all if shorter), mask the rest
  const visibleChars = Math.min(2, localPart.length);
  const maskedLocal = `${localPart.slice(0, visibleChars)}***`;

  return maskedLocal + domain;
}

export function slugifyFilename(filename: string): string {
  const lastDot = filename.lastIndexOf('.');
  const name = lastDot === -1 ? filename : filename.substring(0, lastDot);
  const extension = lastDot === -1 ? '' : filename.substring(lastDot + 1);

  const sluggedName = name
    .toString()
    .toLowerCase()
    // Replace characters that are not alphanumeric, hyphen, or underscore with a hyphen
    .replace(/[\s_.]+/g, '-') // Replace spaces, underscores, dots (in name part) with hyphens
    .replace(/[^a-z0-9-]+/g, '-') // Remove other invalid characters, allowing hyphens
    .replace(/--+/g, '-') // Replace multiple hyphens with a single one
    .replace(/^-+/, '') // Trim hyphens from start
    .replace(/-+$/, ''); // Trim hyphens from end

  if (extension) {
    // Keep extension lowercased and clean (though less critical for extension part)
    const cleanedExtension = extension.toLowerCase().replace(/[^a-z0-9]/g, '');
    return `${sluggedName || 'document'}.${cleanedExtension}`;
  }
  return sluggedName || 'document';
}

/**
 * Truncates a string to a maximum length with ellipsis.
 * Used for display purposes when text needs to fit in constrained UI spaces.
 */
export function truncate(str: string, maxLength: number): string {
  if (str.length <= maxLength) return str;
  return `${str.slice(0, maxLength).trim()}…`;
}
